/**
 * The web client's data layer. Speaks the SAME encrypted Firestore protocol as the phone + server, over the
 * SHARED path builders (`@shared/paths`) and the SHARED `Codec` (`@shared/codec`) — so there's one layout +
 * one seal/open path across all clients. A `PairingClient` holds one pairing's mirrored secret and exposes
 * live subscriptions + sends; content is decrypted locally, Firebase only ever sees ciphertext.
 */
import {
  addDoc,
  arrayUnion,
  deleteDoc,
  getDoc,
  getDocs,
  limit as fbLimit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  type Firestore,
  type QuerySnapshot,
  type Unsubscribe,
} from "firebase/firestore";
import { getBytes, ref as storageRef, uploadString } from "firebase/storage";
import * as paths from "@shared/paths";
import { Codec } from "@shared/codec";
import { bytesToB64, bytesToUtf8 } from "@shared/encoding";
import { storage } from "./firebase";
import { webCrypto } from "./crypto";
import { notifyRead } from "./push";
import type { PairingPayload } from "./pairings";
import { fileLabel } from "./attachments";
import { latestOnly } from "./snapshotOrder";
import { reviewSummary, type DocComment } from "./review";
import type { Mention } from "./mentions";

// ── Persistent attachment-blob cache (IndexedDB) ────────────────────────────
// Attachment blobs are AES-GCM ciphertext and immutable, so caching them across page loads avoids
// re-downloading from Storage (the egress cost) while never persisting decrypted image data.
const IDB_NAME = "failover-att";
const IDB_STORE = "blobs";

function idbOpen(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGetBlob(key: string): Promise<ArrayBuffer | null> {
  const db = await idbOpen();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const req = db.transaction(IDB_STORE, "readonly").objectStore(IDB_STORE).get(key);
      req.onsuccess = () => resolve((req.result as ArrayBuffer) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbPutBlob(key: string, val: ArrayBuffer): Promise<void> {
  const db = await idbOpen();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite").objectStore(IDB_STORE).put(val, key);
      tx.onsuccess = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export interface DecisionCard {
  id: string;
  body?: string;
  options: { id: string; label: string }[];
}

/** The option id a dismissal carries instead of a real choice. Shared with the phone so both clients agree
 *  that this correlation cleared the card rather than answered it. */
export const DISMISSED_OPTION = "__dismissed__";

/** How a decision card ended, derived from the messages that correlate to it. `undefined` = still open.
 *  "expired" is a card the agent's server retracted after the user wrote past it without answering; "ran" and
 *  "failed" are an answered command card whose command then succeeded or failed. */
export type CardState = "answered" | "cleared" | "expired" | "ran" | "failed";

/** Which cards in a message list are no longer open, and how each one ended. A tapped option answers a card;
 *  a `sys:"resolved"` marker (a dismiss on either client, or an agent retraction) merely clears it. The two
 *  must stay distinguishable — a cleared card was never decided, so labelling it "Answered" would be a lie. */
export function cardStates(messages: ChatMessage[]): Map<string, CardState> {
  const states = new Map<string, CardState>();
  for (const message of messages) {
    if (!message.cardId) continue;
    // An answer is the stronger fact: if both exist for one card, it was decided.
    if (message.sys === "resolved" && (message.why === "ran" || message.why === "failed")) {
      states.set(message.cardId, message.why);
    } else if (message.sys === "resolved") {
      if (!states.has(message.cardId)) states.set(message.cardId, message.why === "expired" ? "expired" : "cleared");
    } else {
      const was = states.get(message.cardId);
      if (was !== "ran" && was !== "failed") states.set(message.cardId, "answered");
    }
  }
  return states;
}

/** When a call rang, when it was picked up, when it ended, and how — carried on its recap message. */
export interface CallShape {
  ringTs: number;
  /** When the user picked up, or absent when nobody did. A missed call has no duration, not a zero one. */
  answerTs?: number;
  endTs: number;
  /** "answered", "declined", "missed", or "text" (settled in writing instead). */
  outcome: string;
}

/**
 * The call shape off a sealed payload, or undefined when there isn't a usable one. Mirrors the phone's rule.
 *
 * A block with no end is dropped rather than shown with a blank duration: the absence of the field is not a
 * fact about the call, and a log listing it would be reporting one.
 */
export function parseCallShape(raw: unknown): CallShape | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const c = raw as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined);
  const endTs = num(c.endTs);
  if (!endTs) return undefined;
  const answerTs = num(c.answerTs);
  return {
    ringTs: num(c.ringTs) ?? 0,
    ...(answerTs ? { answerTs } : {}),
    endTs,
    outcome: typeof c.outcome === "string" && c.outcome ? c.outcome : "answered",
  };
}

/** How the call ended and how long they talked — "Answered · 4 min", or just the outcome when nobody picked
 *  up. Only a call that was answered has a length; measuring from the ring would report the time the phone
 *  spent ringing at nobody as time spent talking. Mirrors the Kotlin labels. */
export function callLine(call: CallShape): string {
  const words: Record<string, string> = {
    answered: "Answered",
    declined: "Declined",
    missed: "No answer",
    text: "Settled in chat",
  };
  // An outcome from a newer server shows as it came rather than being dropped — an unknown word beats a gap.
  const outcome = words[call.outcome] ?? call.outcome;
  const ms = call.answerTs ? call.endTs - call.answerTs : 0;
  if (ms <= 0) return outcome;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${outcome} · ${sec} s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${outcome} · ${min} min`;
  return `${outcome} · ${Math.floor(min / 60)} h ${min % 60} min`;
}

export interface ChatMessage {
  id: string;
  role: "user" | "agent";
  text: string;
  ts: number;
  cardId?: string;
  replyTo?: string;
  /** Quick-reply chips (agent → user): tapping sends the label back as a plain reply. */
  options?: string[];
  /** A structured decision card (agent → user): body + labelled buttons; tapping replies with the choice. */
  card?: DecisionCard;
  /** System marker (e.g. "resolved" collapses a card); not rendered as a bubble. */
  sys?: string;
  /** Why a "resolved" marker closed its card: "expired" when the user wrote past it without answering. */
  why?: string;
  /** Storage path + mime of an attachment, if any (loaded lazily via loadImage). */
  att?: string;
  mime?: string;
  /** What a non-image attachment is called. A picture is its own preview and carries none; the Storage
   *  path is a random id by design, so for a file this is the only thing that says what arrived. */
  attName?: string;
  /** Multiple images (a gallery): parallel Storage paths + mimes. Single `att` stays for back-compat. */
  atts?: string[];
  mimes?: string[];
  /** REFERENCE to a document in this channel's documents collection (e.g. a plan) — rendered as a document
   *  card; the content is loaded + decrypted on tap (loadDocument) and shown in a reader. */
  docId?: string;
  /** What that document is called, so the card that opens it has a label of its own. Absent on messages
   *  written before the sender started saying so. */
  docName?: string;
  /** Delivery receipt for outbound (user) messages: sent → delivered → read → ack (server-written). */
  receipt?: "delivered" | "read" | "ack";
  /** GROUP messages only: the sessionId of the agent that posted (absent = the human/observer posted). Lets the
   *  client label who's talking in a multi-participant thread. */
  sender?: string;
  /** GROUP messages only: the sessionId of the single agent this post is addressed TO. A group post is a
   *  request to one owner or an answer to one, so who it is for is half of what it means — a thread showing
   *  only who spoke reads as everyone broadcasting at once. Rides inside the sealed payload, not the doc. */
  to?: string;
  /** GROUP messages only: per-participant receipt map (sessionId → delivered/read/ack) — shows which of the
   *  other agents have seen a message. */
  receipts?: Record<string, "delivered" | "read" | "ack">;
  /** What this message IS when it is not just prose — currently only a call recap. Lets a call be found as a
   *  call rather than by matching the English the agent happened to open the recap with. */
  kind?: string;
  /** For a recap: the call it recaps. Absent on a recap written before the marker existed. */
  call?: CallShape;
  /** For a message exchanged with another agent: which way it went, that agent's name, and its session. */
  peer?: PeerShape;
}

/** PeerShape names the other side of an agent-to-agent message; `session` opens its chat when known. */
export interface PeerShape {
  dir: "in" | "out";
  name: string;
  session?: string;
}

function parsePeer(raw: unknown): PeerShape | undefined {
  const p = raw as { dir?: unknown; name?: unknown; session?: unknown } | undefined;
  if (!p || (p.dir !== "in" && p.dir !== "out") || typeof p.name !== "string") return undefined;
  return { dir: p.dir, name: p.name, ...(typeof p.session === "string" && p.session ? { session: p.session } : {}) };
}

/** A participant in a #Group-chat: a session identity (agent) or the observing human. `label`/`repo` are the
 *  client's resolved display fields (from its session list); only `id` + `kind` are stored. Mirrors the
 *  server's Participant. */
export interface Participant {
  id: string;
  kind: "human" | "agent";
  label: string;
  repo?: string;
}

/** A pairing-level group channel (#Group-chat): agent-to-agent DMs with the human observing. Lives outside any
 *  one session, so it's watched separately from a session's idea channels. */
export interface GroupChannel {
  id: string;
  kind: string;
  title: string;
  participants: Participant[];
  updatedAt: number;
}

/** The observing human's participant id in a group's receipts map. One id for the person, not per device —
 *  that's what lets a group read on the phone show as read here too. Mirrors the Kotlin constant. */
export const HUMAN_PARTICIPANT_ID = "human";

/**
 * Whether this group message opens a new speaker run — i.e. whether it gets the "sender → recipient" label.
 *
 * Only a run's FIRST message is labelled, so a run has to break whenever that label would change: a
 * different speaker, or the same speaker turning to a different peer. Breaking only on the speaker was a
 * real bug on the phone, where one agent's release note rendered under another's name; the addressee half
 * has the same failure, since a group post is a request to ONE owner and two consecutive asks from the same
 * agent can go to different people. Mirrors the Kotlin cluster rule.
 */
export function opensSpeakerRun(m: ChatMessage, prev?: ChatMessage): boolean {
  if (m.role !== "agent" || !m.sender) return false;
  return !prev || prev.role !== "agent" || prev.sender !== m.sender || prev.to !== m.to;
}

/**
 * The ids of the group messages an agent addressed to the observing human.
 *
 * A group thread is mostly agents talking to each other, and an answer meant for the human renders exactly
 * like the traffic around it — so it goes unseen. Two things count as addressed: a post naming the human as
 * its addressee, and an answer quoting something the human said. Both are needed: only the second one occurs
 * while a group post can only be addressed to an agent, and only the first survives if that ever changes.
 *
 * Ids rather than a per-message test so the caller does one pass and then a set lookup per row. Mirrors the
 * Kotlin `addressedToHumanIds`.
 */
export function addressedToHumanIds(messages: ChatMessage[]): Set<string> {
  const fromHuman = new Set(messages.filter((m) => m.role === "user" && m.id).map((m) => m.id));
  return new Set(
    messages
      .filter((m) => m.role === "agent" && !m.sys && m.id && (m.to === HUMAN_PARTICIPANT_ID || (m.replyTo ? fromHuman.has(m.replyTo) : false)))
      .map((m) => m.id),
  );
}

/** Whether the observer has already read this group message (their entry in the per-participant receipts). */
export function readByHuman(m: ChatMessage): boolean {
  const state = m.receipts?.[HUMAN_PARTICIPANT_ID];
  return state === "read" || state === "ack";
}

/** One update per message, so a read of a long displayed window is bounded rather than a burst of writes. */
export const MAX_RECEIPT_WRITES = 200;

/**
 * The displayed messages whose sender hasn't been told they were read yet.
 *
 * `stateOf` reads whatever holds the current receipt — a 1:1 chat keeps one, a group keeps one per
 * participant — so both kinds select the same way.
 *
 * Only agent messages carry a receipt (the user's own are what is being acknowledged TO), and a message with
 * no id has no document to stamp. Over the limit the NEWEST are kept, and the read watermark sent to the
 * user's other devices must be taken from this result rather than from everything pending — otherwise it
 * announces messages read that were never stamped.
 */
export function awaitingReadReceipt(
  messages: ChatMessage[],
  stateOf: (m: ChatMessage) => string | undefined,
  limit = MAX_RECEIPT_WRITES,
): ChatMessage[] {
  return messages
    .filter((m) => m.role === "agent" && !!m.id && stateOf(m) !== "read" && stateOf(m) !== "ack")
    .slice(-limit);
}

/** A group's display name: its explicit title, or — when unnamed (the name is optional) — the participant
 *  labels joined. `resolve` maps a participant to a live label (falls back to the stored roster label). */
export function groupLabel(group: GroupChannel, resolve?: (p: Participant) => string): string {
  if (group.title.trim()) return group.title;
  const names = group.participants
    .filter((p) => p.kind === "agent")
    .map((p) => (resolve ? resolve(p) || p.label : p.label))
    .filter((n) => n.trim());
  return names.length ? names.join(", ") : "Group";
}

export interface SessionContact {
  pairingId: string;
  sessionId: string;
  label: string;
  online: boolean;
  /** Offline, but the machine's fleet can start it: a message sent to it wakes it. */
  asleep: boolean;
  /** The app that starts and resumes this session itself ("conductor"), or "": a message waits until it runs there. */
  host: string;
  /** Derived activity: online but parked on an accepted turn-end → a yellow (vs green) dot. */
  idle: boolean;
  /** Parked specifically on the user's reply → a distinct "awaiting" presence (idea live-activity). */
  awaiting: boolean;
  /** Live "what's the agent doing right now" line from the transcript (idea live-activity). "" = none. */
  activityLine: string;
  /** The agent's latest words in its own reply text, shown with the activity line while it works. "" = none. */
  activityUpdate: string;
  /** Whether an agent is attached to the session's streams. `online` only means the server process is up;
   *  online + NOT attached is "away". Absent (older server) reads as attached, so pre-field sessions
   *  never show away. */
  agentAttached: boolean;
  /** Last heartbeat ts (presence). NOT used for ordering — it bumps every ~30s and would reshuffle the list. */
  lastSeen: number;
  /** Session start ts — the stable sort fallback for sessions with no messages yet. */
  startedAt: number;
  /** Swipe-archived (or auto-reaped) — hidden from the list, like the phone. */
  archived: boolean;
  /** When it was archived — a later incoming main-chat message resurfaces it. 0 = not archived. */
  archivedAt: number;
  /** Whether this checkout is a repository's main one or a linked worktree of it. Absent (older server)
   *  reads as "standalone", which lists exactly as every session listed before the field existed. */
  relationship: "primary" | "child" | "standalone";
  /** The session this one hangs under, when a parent was established. "" = none, and it stays top-level. */
  parentSessionId: string;
  /** The branch the checkout has out — shown on a child, where it is what distinguishes one from another. */
  branch: string;
  /** A worktree child Failover runs for its parent. It has no ideas. */
  managed: boolean;
  /** The prompts the machine's fleet has scheduled for this session, soonest first. Absent = none. */
  scheduled?: ScheduledJob[];
  /** When the fleet last confirmed `scheduled`; 0 when it never said. */
  scheduledSeen?: number;
}

/** ScheduledJob is one prompt the fleet submits to a session on a cron schedule. */
export interface ScheduledJob {
  id: string;
  /** When it runs, in words. */
  schedule: string;
  cron: string;
  prompt: string;
  recurring: boolean;
  nextRun: number;
  /** For a recurring job: its first run after this is its last. */
  expiresAt?: number;
}

export interface IdeaChannel {
  id: string;
  kind: string;
  status: string;
  title: string;
  description: string;
  doc: string;
  updatedAt: number;
}

export interface TodoItem {
  content: string;
  status: string; // pending | in_progress | completed
}

/** One agent a workflow run launched. */
export interface AgentStep {
  label: string;
  phase: string;
  state: "running" | "done";
  doing: string;
  result: string;
}

/** One workflow run in a session, live or recently ended. */
export interface WorkflowRun {
  runId: string;
  title: string;
  name: string;
  summary: string;
  phases: string[];
  phase: string;
  state: "running" | "completed" | "failed" | "killed";
  startedAt: number;
  endedAt?: number;
  agentCount: number;
  agents: AgentStep[];
  tokens?: number;
  toolCalls?: number;
  log?: string;
}

/** One sub-agent spawned outside a workflow. */
export interface SubAgentRun {
  id: string;
  title: string;
  background: boolean;
  worktree: boolean;
  state: "running" | "done";
  doing: string;
  result: string;
  startedAt: number;
  updatedAt: number;
}

/** A session's workflow runs and sub-agents, as its server last published them. */
export interface AgentsSnapshot {
  workflows: WorkflowRun[];
  subagents: SubAgentRun[];
}

const SESSION_STALE_MS = 90_000; // mirror the phone: no heartbeat within this → offline
const ASLEEP_FRESH_MS = 15 * 60_000; // how long the fleet's mark on a sleeping session holds without renewal

/** A live connection for ONE pairing — mirrors the phone: watch its session-contacts + their chats,
 *  decrypting with the mirrored pairing secret through the shared Codec. */
export class PairingClient {
  private constructor(
    private readonly db: Firestore,
    private readonly pairingId: string,
    private readonly codec: Codec<CryptoKey>,
    /** Kept so a read can push the notification withdrawal to the user's other devices. */
    private readonly pairing: PairingPayload,
  ) {}

  static async create(db: Firestore, pairing: PairingPayload): Promise<PairingClient> {
    return new PairingClient(db, pairing.pairingId, await Codec.create(webCrypto, pairing.pairingSecret), pairing);
  }

  /** Watch the pairing's session-contacts (the sidebar list). Presence itself is cleartext; the live activity
   *  line is not — it names files, commands and searches — so each row is opened before it is handed over. */
  subscribeSessions(cb: (sessions: SessionContact[]) => void): Unsubscribe {
    const publish = latestOnly(cb);
    // A server that dies without shutting down never writes again, so presence is recomputed on a clock as
    // well as on each snapshot; otherwise its last "online" would stand until some other session wrote.
    let last: QuerySnapshot | undefined;
    const render = (snap: QuerySnapshot) => {
      const now = Date.now();
      publish(
        Promise.all(
        snap.docs.map(async (d) => {
          const data = d.data();
          const lastSeen = typeof data.lastSeen === "number" ? data.lastSeen : 0;
          const reachable = data.reachable === true;
          const archived = data.archived === true;
          const archivedAt = typeof data.archivedAt === "number" ? data.archivedAt : 0;
          return {
            pairingId: this.pairingId,
            sessionId: d.id,
            label: typeof data.label === "string" && data.label ? data.label : "Session",
            online: reachable && now - lastSeen < SESSION_STALE_MS,
            asleep:
              !(reachable && now - lastSeen < SESSION_STALE_MS) &&
              typeof data.asleepSeen === "number" &&
              now - data.asleepSeen < ASLEEP_FRESH_MS,
            host: typeof data.host === "string" ? data.host : "",
            idle: data.activity === "idle",
            awaiting: data.activity === "awaiting",
            ...(await this.activityOf(data)),
            agentAttached: data.agentAttached !== false, // absent = older server → treat as attached
            lastSeen,
            startedAt: typeof data.startedAt === "number" ? data.startedAt : 0,
            archived,
            archivedAt,
            relationship:
              data.relationship === "primary" || data.relationship === "child" ? data.relationship : "standalone",
            parentSessionId: typeof data.parentSessionId === "string" ? data.parentSessionId : "",
            branch: typeof data.branch === "string" ? data.branch : "",
            managed: data.managed === true,
            ...(await this.scheduledOf(data)),
          } satisfies SessionContact;
          }),
        ),
      );
    };
    const unsubscribe = onSnapshot(paths.sessionsCol(this.db, this.pairingId), (snap) => {
      last = snap;
      render(snap);
    });
    const ticker = setInterval(() => last && render(last), SESSION_STALE_MS / 3);
    return () => {
      clearInterval(ticker);
      unsubscribe();
    };
  }

  /**
   * What the agent is doing right now, from the sealed field if there is one.
   *
   * The plaintext field is still read as a fallback for a server that predates the sealed one, and a
   * ciphertext this browser cannot open yields no line rather than a placeholder — the row has a perfectly
   * good thing to say without it.
   */
  private async activityOf(data: Record<string, unknown>): Promise<{ activityLine: string; activityUpdate: string }> {
    if (typeof data.activityCt === "string" && data.activityCt) {
      const opened = await this.codec.open<{ line?: unknown; update?: unknown }>(data.activityCt).catch(() => null);
      return {
        activityLine: typeof opened?.line === "string" ? opened.line : "",
        activityUpdate: typeof opened?.update === "string" ? opened.update : "",
      };
    }
    return { activityLine: typeof data.activityLine === "string" ? data.activityLine : "", activityUpdate: "" };
  }

  private async scheduledOf(data: Record<string, unknown>): Promise<{ scheduled?: ScheduledJob[]; scheduledSeen?: number }> {
    if (typeof data.scheduledCt !== "string" || !data.scheduledCt) return {};
    const cancelled = new Set(Array.isArray(data.scheduledCancel) ? data.scheduledCancel : []);
    const opened = await this.codec.open<unknown>(data.scheduledCt).catch(() => null);
    if (!Array.isArray(opened)) return {};
    const jobs = opened.filter(
      (j): j is ScheduledJob =>
        !!j && typeof j.id === "string" && typeof j.prompt === "string" && typeof j.nextRun === "number" && !cancelled.has(j.id),
    );
    return jobs.length ? { scheduled: jobs, scheduledSeen: typeof data.scheduledSeen === "number" ? data.scheduledSeen : 0 } : {};
  }

  /** cancelScheduled asks the machine's fleet to delete scheduled job `id` of session `sessionId`. */
  async cancelScheduled(sessionId: string, id: string): Promise<void> {
    await updateDoc(paths.sessionDoc(this.db, this.pairingId, sessionId), { scheduledCancel: arrayUnion(id) });
  }

  /** Decode + order one message-collection snapshot — shared by the main chat and idea sub-channels.
   *  Loads the LAST `max` by ts (descending + limit), re-sorted ascending for display — so huge histories
   *  don't all load at once; the caller raises `max` to page in older messages. */
  private watchMessages(
    col: ReturnType<typeof paths.mainMsgCol>,
    cb: (messages: ChatMessage[]) => void,
    max: number,
  ): Unsubscribe {
    const publish = latestOnly(cb);
    return onSnapshot(query(col, orderBy("ts", "desc"), fbLimit(max)), (snap) => {
      publish(
        Promise.all(
        snap.docs.map(async (d): Promise<ChatMessage | null> => {
          const data = d.data() as { role?: unknown; ct?: unknown; ts?: unknown; receipt?: unknown; sender?: unknown; receipts?: unknown };
          const parsed =
            typeof data.ct === "string"
              ? await this.codec.open<{
                  text?: unknown;
                  cardId?: unknown;
                  replyTo?: unknown;
                  att?: unknown;
                  mime?: unknown;
                  name?: unknown;
                  atts?: unknown;
                  mimes?: unknown;
                  options?: unknown;
                  card?: unknown;
                  sys?: unknown;
                  why?: unknown;
                  docId?: unknown;
                  docName?: unknown;
                  kind?: unknown;
                  call?: unknown;
                  to?: unknown;
                  peer?: unknown;
                }>(data.ct)
              : null;
          if (!parsed) return null;
          const text = String(parsed.text ?? "");
          const att = typeof parsed.att === "string" ? parsed.att : undefined;
          const atts = Array.isArray(parsed.atts)
            ? parsed.atts.filter((a): a is string => typeof a === "string")
            : undefined;
          const mimes = Array.isArray(parsed.mimes)
            ? parsed.mimes.filter((m): m is string => typeof m === "string")
            : undefined;
          const options = Array.isArray(parsed.options)
            ? parsed.options.filter((o): o is string => typeof o === "string")
            : undefined;
          const cardRaw = parsed.card as { id?: unknown; body?: unknown; options?: unknown } | undefined;
          const card =
            cardRaw && typeof cardRaw.id === "string" && Array.isArray(cardRaw.options)
              ? {
                  id: cardRaw.id,
                  ...(typeof cardRaw.body === "string" ? { body: cardRaw.body } : {}),
                  options: (cardRaw.options as unknown[])
                    .map((o) => o as { id?: unknown; label?: unknown })
                    .filter((o) => typeof o.id === "string" && typeof o.label === "string")
                    .map((o) => ({ id: o.id as string, label: o.label as string })),
                }
              : undefined;
          const docId = typeof parsed.docId === "string" && parsed.docId.trim() ? parsed.docId : undefined;
          const docName = typeof parsed.docName === "string" && parsed.docName.trim() ? parsed.docName : undefined;
          const sys = typeof parsed.sys === "string" && parsed.sys ? parsed.sys : undefined;
          const kind = typeof parsed.kind === "string" && parsed.kind ? parsed.kind : undefined;
          const call = parseCallShape(parsed.call);
          // A system marker IS content — it is how a card is retracted or dismissed. Dropping it here is why
          // web kept showing cards as live and tappable after the agent retracted them or the phone cleared
          // them. The phone's parser counts `sys` for exactly this reason; match it rather than diverge.
          const hasContent = text.trim() || att || (atts && atts.length) || (options && options.length) || card || docId || sys;
          if (!hasContent) return null; // truly empty
          return {
            id: d.id,
            role: data.role === "user" ? "user" : "agent",
            text,
            ts: typeof data.ts === "number" ? data.ts : 0,
            ...(typeof parsed.cardId === "string" ? { cardId: parsed.cardId } : {}),
            ...(typeof parsed.replyTo === "string" ? { replyTo: parsed.replyTo } : {}),
            ...(att
              ? {
                  att,
                  mime: typeof parsed.mime === "string" ? parsed.mime : "image/*",
                  ...(typeof parsed.name === "string" && parsed.name.trim() ? { attName: parsed.name } : {}),
                }
              : {}),
            ...(atts && atts.length ? { atts, mimes: mimes ?? [] } : {}),
            ...(options && options.length ? { options } : {}),
            ...(card ? { card } : {}),
            ...(docId ? { docId } : {}),
            ...(docName ? { docName } : {}),
            ...(sys ? { sys } : {}),
            ...(typeof parsed.why === "string" && parsed.why ? { why: parsed.why } : {}),
            ...(data.receipt === "delivered" || data.receipt === "read" || data.receipt === "ack"
              ? { receipt: data.receipt }
              : {}),
            ...(typeof data.sender === "string" && data.sender ? { sender: data.sender } : {}),
            ...(typeof parsed.to === "string" && parsed.to ? { to: parsed.to } : {}),
            ...(data.receipts && typeof data.receipts === "object"
              ? { receipts: data.receipts as Record<string, "delivered" | "read" | "ack"> }
              : {}),
            ...(kind ? { kind } : {}),
            ...(call ? { call } : {}),
            ...(kind === "peer" && parsePeer(parsed.peer) ? { peer: parsePeer(parsed.peer) } : {}),
          };
        }),
        ).then((msgs) => msgs.filter((m): m is ChatMessage => m !== null).sort((a, b) => a.ts - b.ts)),
      );
    });
  }

  /** Watch a session's MAIN chat, decrypted + ordered (last `max` messages). */
  subscribeChat(sessionId: string, cb: (messages: ChatMessage[]) => void, max = 50): Unsubscribe {
    return this.watchMessages(paths.mainMsgCol(this.db, this.pairingId, sessionId), cb, max);
  }

  /**
   * Stamp `read` on the agent messages this browser has displayed. That receipt is the shared read signal:
   * the phone derives its unread badge from it and withdraws its notification, so reading here clears there.
   * Only the receipt field changes, which is the one update the rules permit on a message.
   */
  async markMessagesRead(sessionId: string, messages: ChatMessage[]): Promise<void> {
    const pending = awaitingReadReceipt(messages, (m) => m.receipt);
    if (!pending.length) return;
    await Promise.all(
      pending.map((m) =>
        updateDoc(paths.channelMsgDoc(this.db, this.pairingId, sessionId, paths.MAIN_CHANNEL, m.id), {
          receipt: "read",
        }).catch(() => {}),
      ),
    );
    // A device whose app isn't running can't observe those receipts, so push the withdrawal too — carrying
    // how far we read, so a device holding a newer message keeps its notification for it. Taken from what was
    // actually stamped, so it can't run past them.
    void notifyRead(this.pairing, sessionId, Math.max(...pending.map((m) => m.ts)));
  }

  /**
   * Stamp the observer's `read` on the agent messages this browser has displayed in a GROUP. A group has
   * several readers, so this merges the human's entry into the per-participant `receipts` map (dot-path, so
   * the other participants' entries survive) rather than writing the 1:1 scalar `receipt` field.
   */
  async markGroupMessagesRead(groupId: string, messages: ChatMessage[]): Promise<void> {
    const pending = awaitingReadReceipt(messages, (m) => m.receipts?.[HUMAN_PARTICIPANT_ID]);
    if (!pending.length) return;
    await Promise.all(
      pending.map((m) =>
        updateDoc(paths.groupMsgDoc(this.db, this.pairingId, groupId, m.id), {
          [`receipts.${HUMAN_PARTICIPANT_ID}`]: "read",
        }).catch(() => {}),
      ),
    );
  }

  /** Watch one idea sub-channel's discussion thread (last `max` messages). */
  subscribeChannelChat(
    sessionId: string,
    channelId: string,
    cb: (messages: ChatMessage[]) => void,
    max = 50,
  ): Unsubscribe {
    return this.watchMessages(paths.channelMsgCol(this.db, this.pairingId, sessionId, channelId), cb, max);
  }

  /** Load + decrypt a referenced document's markdown content on demand (CHANNELS.md §Documents) — the
   *  chat message only carries the doc id, so the reader fetches the body when the user opens it. Pass the
   *  channel id ("main" for the main chat). Null on any miss/failure. */
  async loadDocument(sessionId: string, channelId: string, docId: string): Promise<string | null> {
    try {
      const snap = await getDoc(paths.channelDocDoc(this.db, this.pairingId, sessionId, channelId, docId));
      const ct = snap.data()?.ct;
      if (typeof ct !== "string") return null;
      const parsed = await this.codec.open<{ content?: unknown }>(ct);
      return parsed && typeof parsed.content === "string" ? parsed.content : null;
    } catch {
      return null;
    }
  }

  /**
   * Every revision of a document, oldest first — what the reader's version switcher offers.
   *
   * The row the message points at may be any revision, so the thread is resolved from it first. Ordering is
   * done here rather than in the query: an equality filter alone needs no composite index, while adding an
   * orderBy on a different field does, and an index that has to be kept alive for a list this short is a
   * liability rather than an optimisation. A document written before revisions existed has neither field and
   * is returned as the single revision it is.
   */
  async loadDocumentRevisions(
    sessionId: string,
    channelId: string,
    docId: string,
  ): Promise<Array<{ id: string; rev: number; ts: number; name: string }>> {
    try {
      const self = await getDoc(paths.channelDocDoc(this.db, this.pairingId, sessionId, channelId, docId));
      const data = self.data();
      if (!data) return [];
      const thread = typeof data.thread === "string" && data.thread ? data.thread : null;
      const one = [{ id: docId, rev: typeof data.rev === "number" ? data.rev : 1, ts: Number(data.ts ?? 0), name: String(data.name ?? "") }];
      if (!thread) return one;
      const snap = await getDocs(
        query(paths.channelDocsCol(this.db, this.pairingId, sessionId, channelId), where("thread", "==", thread)),
      );
      const rows = snap.docs.map((d) => ({
        id: d.id,
        rev: typeof d.data().rev === "number" ? (d.data().rev as number) : 1,
        ts: Number(d.data().ts ?? 0),
        name: String(d.data().name ?? ""),
      }));
      return rows.length ? rows.sort((a, b) => a.rev - b.rev || a.ts - b.ts) : one;
    } catch {
      return [];
    }
  }

  /**
   * Submit a REVIEW: every pending comment at once, followed by a single chat message announcing it.
   *
   * One submit, one message — a comment per bubble would bury the thread, and the agent wants the round, not
   * a drip. The comments are their own docs because they are resolved individually and outlive the revision
   * they were written on, which a payload inside an immutable message could not do.
   */
  async submitReview(
    sessionId: string,
    channelId: string,
    doc: { id: string; thread: string; rev: number; name: string },
    comments: Array<{ quote: string; text: string; parent?: string }>,
  ): Promise<number> {
    if (!comments.length) return 0;
    let written = 0;
    let replies = 0;
    for (const c of comments) {
      const ct = await this.codec.seal({
        quote: c.quote.slice(0, 2000),
        text: c.text.slice(0, 4000),
        docId: doc.id,
        ...(c.parent ? { parent: c.parent } : {}),
      });
      await addDoc(paths.channelCommentsCol(this.db, this.pairingId, sessionId, channelId), {
        thread: doc.thread,
        rev: doc.rev,
        ct,
        ts: Date.now(),
        status: "open",
        author: "user",
      })
        .then(() => {
          written++;
          if (c.parent) replies++;
        })
        .catch(() => {});
    }
    if (!written) return 0;
    // The message says what happened and where; the comments themselves are read from their own collection,
    // so this stays short whether the round is one comment or twenty. It lands in the channel the document
    // lives in, which is where the agent that wrote it is listening.
    const summary = reviewSummary(written - replies, replies, doc);
    await this.sendChannelMessage(sessionId, channelId, summary);
    return written;
  }

  /** Every review comment on a document thread, oldest first — what the reader shows alongside the text. */
  async loadComments(
    sessionId: string,
    channelId: string,
    thread: string,
  ): Promise<DocComment[]> {
    try {
      const snap = await getDocs(
        query(paths.channelCommentsCol(this.db, this.pairingId, sessionId, channelId), where("thread", "==", thread)),
      );
      const out = await Promise.all(
        snap.docs.map(async (d): Promise<DocComment | null> => {
          const raw = d.data();
          const body = await this.codec.open<{ quote?: unknown; text?: unknown; parent?: unknown }>(String(raw.ct ?? ""));
          if (!body) return null;
          return {
            id: d.id,
            rev: typeof raw.rev === "number" ? raw.rev : 1,
            ts: Number(raw.ts ?? 0),
            status: String(raw.status ?? "open"),
            author: String(raw.author ?? "user"),
            quote: typeof body.quote === "string" ? body.quote : "",
            text: typeof body.text === "string" ? body.text : "",
            ...(typeof body.parent === "string" && body.parent ? { parent: body.parent } : {}),
          };
        }),
      );
      // Sorted here rather than in the query: an equality filter alone needs no composite index.
      return out.filter((c): c is DocComment => !!c).sort((a, b) => a.ts - b.ts);
    } catch {
      return [];
    }
  }

  /** Watch a session's channels: decrypt each title/description/spec-doc, and JOIN the status+order from the
   *  separate backlog/{id} collection (no longer on the channel doc). Watches both collections and re-emits
   *  when either changes. The main chat is one of these channels and arrives with the rest — callers that
   *  want ideas select them by kind. */
  subscribeChannels(sessionId: string, cb: (channels: IdeaChannel[]) => void): Unsubscribe {
    let base: Array<Omit<IdeaChannel, "status">> = [];
    const backlog = new Map<string, { status: string; order: number }>();
    let channelsReady = false;
    const emit = () => {
      if (!channelsReady) return;
      const joined = base.map((c) => ({ ch: { ...c, status: backlog.get(c.id)?.status ?? "draft" }, order: backlog.get(c.id)?.order ?? 0 }));
      joined.sort((a, b) => a.order - b.order || b.ch.updatedAt - a.ch.updatedAt);
      cb(joined.map((x) => x.ch));
    };
    const unsubCh = onSnapshot(paths.channelsCol(this.db, this.pairingId, sessionId), (snap) => {
      void Promise.all(
        snap.docs.map(async (d): Promise<Omit<IdeaChannel, "status">> => {
          const data = d.data();
          const meta =
            typeof data.ct === "string"
              ? await this.codec.open<{ title?: unknown; description?: unknown }>(data.ct)
              : null;
          const docObj = typeof data.docCt === "string" ? await this.codec.open<{ doc?: unknown }>(data.docCt) : null;
          return {
            id: d.id,
            // The main chat is what it is by identity. Read off the record it would fall back to "idea"
            // whenever that record was created by something other than the seed — an attachment write, say.
            kind: d.id === paths.MAIN_CHANNEL ? "main" : String(data.kind ?? "idea"),
            title: String(meta?.title ?? ""),
            description: String(meta?.description ?? ""),
            doc: String(docObj?.doc ?? ""),
            updatedAt: typeof data.updatedAt === "number" ? data.updatedAt : typeof data.ts === "number" ? data.ts : 0,
          };
        }),
      ).then((channels) => {
        base = channels;
        channelsReady = true;
        emit();
      });
    });
    const unsubBl = onSnapshot(paths.backlogCol(this.db, this.pairingId, sessionId), (snap) => {
      backlog.clear();
      for (const d of snap.docs) {
        const data = d.data();
        backlog.set(d.id, {
          status: typeof data.status === "string" ? data.status : "draft",
          order: typeof data.order === "number" ? data.order : 0,
        });
      }
      emit();
    });
    return () => {
      unsubCh();
      unsubBl();
    };
  }

  /** Watch a session's live to-do checklist (single encrypted doc, overwritten each update). Read-only:
   *  the agent owns the list; this mirrors it for the user, exactly like the phone app. */
  subscribeTodos(sessionId: string, cb: (todos: TodoItem[]) => void): Unsubscribe {
    return onSnapshot(paths.stateDoc(this.db, this.pairingId, sessionId, "todos"), (snap) => {
      const data = snap.data() as { ct?: unknown } | undefined;
      if (!data || typeof data.ct !== "string") {
        cb([]);
        return;
      }
      void this.codec.open<{ todos?: unknown }>(data.ct).then((parsed) => {
        const list = Array.isArray(parsed?.todos) ? (parsed!.todos as unknown[]) : [];
        cb(
          list
            .map((t) => {
              const o = t as { content?: unknown; status?: unknown };
              return { content: String(o?.content ?? ""), status: String(o?.status ?? "pending") };
            })
            .filter((t) => t.content.trim()),
        );
      });
    });
  }

  /** Watch a session's workflow runs and sub-agents (single encrypted doc, overwritten as they progress). */
  subscribeAgents(sessionId: string, cb: (agents: AgentsSnapshot) => void): Unsubscribe {
    return onSnapshot(paths.stateDoc(this.db, this.pairingId, sessionId, "agents"), (snap) => {
      const data = snap.data() as { ct?: unknown } | undefined;
      if (!data || typeof data.ct !== "string") {
        cb({ workflows: [], subagents: [] });
        return;
      }
      void this.codec.open<Partial<AgentsSnapshot>>(data.ct).then((parsed) => {
        cb({
          workflows: Array.isArray(parsed?.workflows) ? parsed!.workflows : [],
          subagents: Array.isArray(parsed?.subagents) ? parsed!.subagents : [],
        });
      });
    });
  }

  // ── Group chat (#Group-chat): pairing-level, multi-participant channels ──────────────────────────────────

  /** Watch this pairing's group channels. Groups live outside any session (pairings/{pid}/groups), so they're a
   *  separate subscription from a session's ideas. Decrypts {title, participants} from the sealed `ct`; the
   *  participant labels are stored, but the caller may re-resolve them against its live session list. */
  subscribeGroups(cb: (groups: GroupChannel[]) => void): Unsubscribe {
    return onSnapshot(paths.groupsCol(this.db, this.pairingId), (snap) => {
      void Promise.all(
        snap.docs.map(async (d): Promise<GroupChannel> => {
          const data = d.data();
          const meta =
            typeof data.ct === "string"
              ? await this.codec.open<{ title?: unknown; participants?: unknown }>(data.ct)
              : null;
          const participants = Array.isArray(meta?.participants)
            ? (meta!.participants as unknown[])
                .map((p) => p as { id?: unknown; kind?: unknown; label?: unknown; repo?: unknown })
                .filter((p) => typeof p.id === "string")
                .map((p) => ({
                  id: p.id as string,
                  kind: p.kind === "human" ? ("human" as const) : ("agent" as const),
                  label: String(p.label ?? ""),
                  ...(typeof p.repo === "string" ? { repo: p.repo } : {}),
                }))
            : [];
          return {
            id: d.id,
            kind: String(data.kind ?? "group"),
            title: String(meta?.title ?? ""),
            participants,
            updatedAt: typeof data.updatedAt === "number" ? data.updatedAt : typeof data.ts === "number" ? data.ts : 0,
          };
        }),
      ).then((groups) => cb(groups.sort((a, b) => b.updatedAt - a.updatedAt)));
    });
  }

  /** Live messages of one group channel (same decode as any channel; group docs also carry sender/receipts). */
  subscribeGroupMessages(groupId: string, cb: (msgs: ChatMessage[]) => void, max = 200): Unsubscribe {
    return this.watchMessages(paths.groupMsgCol(this.db, this.pairingId, groupId), cb, max);
  }

  /** Create a group and invite participants (the user's action — agents never create groups). Seals the title +
   *  participant identities into `ct`; the human owner is implicitly a participant (observer) and is added here so
   *  the roster is complete. `agentSessionIds` are the invited agents' session ids. */
  async createGroup(title: string, agentSessionIds: string[], roster: Map<string, { label: string; repo?: string }>): Promise<string> {
    const participants: Participant[] = agentSessionIds
      .filter((id) => id)
      .map((id) => ({ id, kind: "agent" as const, label: roster.get(id)?.label ?? "", ...(roster.get(id)?.repo ? { repo: roster.get(id)!.repo! } : {}) }));
    // An unnamed group stores an EMPTY title — each client derives its display name from the participants.
    const ct = await this.codec.seal({ title: title.trim(), participants });
    const now = Date.now();
    const ref = await addDoc(paths.groupsCol(this.db, this.pairingId), { kind: "group", ct, ts: now, updatedAt: now });
    return ref.id;
  }

  /**
   * Close (delete) a group: its messages, then the group doc itself.
   *
   * The messages have to go explicitly. Firestore doesn't cascade, and nothing reaps a group — the session
   * reap works down from a session doc and a group is a sibling of sessions, not under one. Dropping only
   * the group doc hides the thread while leaving every message in it stored indefinitely, which is not what
   * deleting a conversation means. Removing a session already wipes its messages the same way.
   */
  /** Dropping the doc is the whole delete: a backend trigger takes the messages with it, which also
   *  survives a client that loses its connection halfway through. */
  async deleteGroup(groupId: string): Promise<void> {
    await deleteDoc(paths.groupDoc(this.db, this.pairingId, groupId));
  }

  /** Post a message into a group as the human (observer/director). Human posts carry no `sender` — agents
   *  identify their own posts by sender===their sessionId, so a sender-less post reaches every agent. */
  async sendGroupMessage(groupId: string, text: string, replyTo?: string): Promise<void> {
    const t = text.trim();
    if (!t) return;
    const ct = await this.sealReply(t, replyTo ? { replyTo } : undefined);
    await addDoc(paths.groupMsgCol(this.db, this.pairingId, groupId), { role: "user", ct, ts: Date.now() });
    await setDoc(paths.groupDoc(this.db, this.pairingId, groupId), { updatedAt: Date.now() }, { merge: true });
  }

  /** Reply/correlation extras a user message may carry: a decision-card answer (cardId+optionId) or a quote. */
  private sealReply(
    text: string,
    extra?: { cardId?: string; optionId?: string; replyTo?: string; sys?: string; mentions?: Mention[] },
  ): Promise<string> {
    return this.codec.seal({
      text,
      ...(extra?.mentions?.length ? { mentions: extra.mentions } : {}),
      ...(extra?.cardId ? { cardId: extra.cardId } : {}),
      ...(extra?.optionId ? { optionId: extra.optionId } : {}),
      ...(extra?.replyTo ? { replyTo: extra.replyTo } : {}),
      ...(extra?.sys ? { sys: extra.sys } : {}),
    });
  }

  /** Clear a decision card without answering it. Writes the SAME textless marker the phone writes, so a card
   *  dismissed on either client collapses on both. The agent's question stays unanswered — this correlates a
   *  card to "no longer on screen", it does not decide it. Goes through its own method because the ordinary
   *  send paths refuse an empty message, which is exactly what this is. */
  async dismissCard(sessionId: string, cardId: string, channelId?: string): Promise<void> {
    const ct = await this.sealReply("", { cardId, optionId: DISMISSED_OPTION, sys: "resolved" });
    await addDoc(this.msgCol(sessionId, channelId), { role: "user", ct, ts: Date.now() });
  }

  /** Send a user message into a session's main chat (encrypted with the shared Codec). */
  async sendMessage(
    sessionId: string,
    text: string,
    extra?: { cardId?: string; optionId?: string; replyTo?: string; mentions?: Mention[] },
  ): Promise<void> {
    const t = text.trim();
    if (!t) return;
    const ct = await this.sealReply(t, extra);
    await addDoc(this.msgCol(sessionId), { role: "user", ct, ts: Date.now() });
  }

  /** Archive a session (hide it from the list) — merges archived onto the session doc, like the phone swipe. */
  /** Ask `sessionId` to start a background agent; its server takes the request and reports in the chat. */
  async requestAgent(sessionId: string, agent: { label: string; task: string; worktree: boolean }): Promise<void> {
    const ct = await this.codec.seal(agent);
    await addDoc(paths.agentRequestsCol(this.db, this.pairingId, sessionId), { ct, ts: Date.now() });
  }

  async archiveSession(sessionId: string): Promise<void> {
    await setDoc(
      paths.sessionDoc(this.db, this.pairingId, sessionId),
      { archived: true, archivedAt: Date.now() },
      { merge: true },
    );
  }

  /** Un-archive a session — clears the archived flag locally when a new main-chat message resurfaces it. */
  async unarchiveSession(sessionId: string): Promise<void> {
    await setDoc(
      paths.sessionDoc(this.db, this.pairingId, sessionId),
      { archived: false, archivedAt: 0 },
      { merge: true },
    );
  }

  /** Change an idea's status (draft → ready → in_progress → done/rejected). Cleartext status field on the
   *  channel doc, same as the phone writes. */
  async setIdeaStatus(sessionId: string, channelId: string, status: string): Promise<void> {
    // Status lives in the backlog collection now, not the channel doc.
    await setDoc(paths.backlogDoc(this.db, this.pairingId, sessionId, channelId), { status, updatedAt: Date.now() }, { merge: true });
  }

  /** Send a user message into an idea sub-channel. */
  async sendChannelMessage(
    sessionId: string,
    channelId: string,
    text: string,
    extra?: { cardId?: string; optionId?: string; replyTo?: string },
  ): Promise<void> {
    const t = text.trim();
    if (!t) return;
    const ct = await this.sealReply(t, extra);
    await addDoc(this.msgCol(sessionId, channelId), { role: "user", ct, ts: Date.now() });
  }

  private readonly imageCache = new Map<string, string>();

  /** Download + decrypt an image attachment → a `data:` URL. Two-tier cache: the decrypted data URL is
   *  memoised in memory for the page's lifetime, and the IMMUTABLE encrypted blob is persisted in IndexedDB
   *  so a reload decrypts from disk instead of re-downloading from Storage (only ciphertext is stored, never
   *  the decrypted image). The Storage object is the AES-GCM ciphertext of the image's base64 — same scheme
   *  the phone/server use. */
  async loadImage(att: string, mime: string): Promise<string | null> {
    const cached = this.imageCache.get(att);
    if (cached) return cached;
    try {
      let raw = await idbGetBlob(att);
      if (!raw) {
        raw = await getBytes(storageRef(storage, att), 25 * 1024 * 1024);
        void idbPutBlob(att, raw);
      }
      const dataB64 = await this.codec.openRaw(bytesToUtf8(new Uint8Array(raw)));
      if (!dataB64) return null;
      const url = `data:${mime};base64,${dataB64}`;
      this.imageCache.set(att, url);
      return url;
    } catch {
      return null;
    }
  }

  /** Send an image into a session's main chat — or an idea sub-channel when `channelId` is given: encrypt the
   *  bytes to Storage as opaque ciphertext, then post a message carrying the encrypted caption + mime + Storage
   *  path (no plaintext, no files touch Firestore). */
  async sendImage(sessionId: string, file: File, caption = "", channelId?: string): Promise<void> {
    const path = await this.uploadEncrypted(sessionId, file);
    const ct = await this.codec.seal({ text: caption, mime: file.type || "image/*", att: path });
    await addDoc(this.msgCol(sessionId, channelId), { role: "user", ct, ts: Date.now() });
  }

  /**
   * Send a file that is not a picture — the same encrypted upload, carrying the name it shows as.
   *
   * The name rides INSIDE the sealed payload rather than beside it: the Storage path is a random id so the
   * bucket describes nothing it holds, which also means it can never say what the file was called.
   */
  async sendFile(sessionId: string, file: File, caption = "", channelId?: string): Promise<void> {
    const path = await this.uploadEncrypted(sessionId, file);
    const ct = await this.codec.seal({
      text: caption,
      mime: file.type || "application/octet-stream",
      att: path,
      name: fileLabel(file.name, file.type),
    });
    await addDoc(this.msgCol(sessionId, channelId), { role: "user", ct, ts: Date.now() });
  }

  /** Send several images as one gallery message (encrypted `atts`/`mimes`). Falls back to sendImage for one. */
  async sendImages(sessionId: string, files: File[], caption = "", channelId?: string): Promise<void> {
    if (files.length === 0) return;
    if (files.length === 1) return this.sendImage(sessionId, files[0], caption, channelId);
    const atts = await Promise.all(files.map((f) => this.uploadEncrypted(sessionId, f)));
    const mimes = files.map((f) => f.type || "image/*");
    const ct = await this.codec.seal({ text: caption, atts, mimes });
    await addDoc(this.msgCol(sessionId, channelId), { role: "user", ct, ts: Date.now() });
  }

  /** Encrypt + upload one image's bytes to Storage as opaque ciphertext; returns its Storage path. */
  private async uploadEncrypted(sessionId: string, file: File): Promise<string> {
    const dataB64 = bytesToB64(new Uint8Array(await file.arrayBuffer()));
    const path = `attachments/${this.pairingId}/sessions/${sessionId}/${crypto.randomUUID().replace(/-/g, "")}`;
    const blobCt = await this.codec.sealRaw(dataB64);
    await uploadString(storageRef(storage, path), blobCt, "raw", { contentType: "application/octet-stream" });
    return path;
  }

  private msgCol(sessionId: string, channelId?: string) {
    return channelId
      ? paths.channelMsgCol(this.db, this.pairingId, sessionId, channelId)
      : paths.mainMsgCol(this.db, this.pairingId, sessionId);
  }
}
